import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';

import Klass from '@/models/Klass';
import ScheduledLesson from '@/models/ScheduledLesson';
import { ROLES } from '@/utils/roles';
import { ROUTES } from '@/utils/routes';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Tooltip } from '@/components/ui/custom/dataDisplay/Tooltip';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import { ActivityColorIcon } from '@/components/icons';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import { ManageScheduledLessonState } from './ManageScheduledLesson';

type ReportButtonProps = {
  render?: boolean;
  disabled?: boolean;
  klass: Klass;
  scheduledLesson: ScheduledLesson;
  state?: ManageScheduledLessonState;
};

export function ReportButton({
  disabled,
  klass,
  scheduledLesson,
  render,
  state = 'manager',
}: ReportButtonProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.admin.manageKlass',
  });

  const { id: klassId } = klass;

  const { id: scheduledLessonId, isActive } = scheduledLesson;

  const tooltipMessage = disabled ? t('noStudentsInClass') : t('report');

  const delayed = ROLES.SCHEDULED_LESSON.isDelayed({ klass, scheduledLesson });

  const reportLink = disabled
    ? undefined
    : ROUTES.CLASS.LESSON_REPORT(klassId, scheduledLessonId);

  return (
    <ConditionalRenderer condition={render}>
      <div className="flex gap-2 items-center">
        <Tooltip text={tooltipMessage}>
          <IconButton
            href={reportLink}
            disabled={disabled}
            className={twJoin(
              'bg-base-100 rounded-full border hover:ring-3 ring-primary-content hover:bg-primary-content disabled:cursor-not-allowed transition ease-in-out duration-200 transform hover:scale-105',
              delayed ? 'border-warning' : 'border-primary',
              state === 'manager' ? 'p-0.5' : 'p-0.25',
            )}
            icon={
              <ActivityColorIcon
                className={state === 'manager' ? 'w-6 h-6' : 'w-5 h-5'}
              />
            }
          />
        </Tooltip>
        <ConditionalRenderer condition={delayed && isActive}>
          <Tag
            color="warning"
            className={{
              base: twJoin(
                'text-[0.875rem] gap-1 lowercase px-1',
                state === 'manager' ? 'py-0.25' : 'py-0',
              ),
            }}
          >
            {t('delayedReport')}
          </Tag>
        </ConditionalRenderer>
      </div>
    </ConditionalRenderer>
  );
}
